import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * The card a product is drawn on, wherever it appears.
 *
 * The shop and the Mum Hub drew the same card twice. They were meant to match
 * and they did not: two frames, two image ratios, two ways of striking a sale
 * price through, two hover treatments. Adrian, looking at the Mum Hub: "reuse
 * the product cards from the shop page." So there is one frame now, and the
 * two surfaces differ only in what they hang on it.
 *
 * ## What is shared and what is a slot
 *
 * Shared: the frame, the photograph block with its arrows, the brand line in
 * caps, the name, and the price line with the regular price struck through
 * beside what she pays. Those are the things that looked different and should
 * not have.
 *
 * Everything else is a slot, because the two surfaces genuinely differ.
 * The shop hangs structured data, a save button, attribute badges and a "Shop
 * at" button on it. The Mum Hub hangs a "Free to see" chip on it and makes the
 * whole card the shop link. Neither belongs on the other.
 *
 * ## The whole card can be the link
 *
 * Adrian, testing the Mum Hub on a phone: "they should open the store straight
 * when clicking on them, not the product card." So `slideLink` and `wordsLink`
 * turn the photographs and the words into anchors. Two anchors rather than one
 * wrapping the lot: an anchor over the photograph row would swallow the swipe,
 * and the arrows are buttons, which may not sit inside an anchor at all.
 *
 * The shop keeps its button instead and passes neither, which is why its
 * markup is unchanged to the character. `ProductCardMarkup.test.tsx` is what
 * holds that.
 *
 * ## A swipe is not a tap
 *
 * The same finger scrolls the photographs and opens a shop. A drag that ends
 * on a photograph would otherwise navigate, so every swipe through the gallery
 * throws her out to a merchant. The pointer's travel is measured and past
 * {@link TAP_DRAG_THRESHOLD_PX} the click is cancelled. Eight pixels rather
 * than zero, because a real tap on a touchscreen moves a little.
 */

/** How far a pointer may travel and still count as a tap, in CSS pixels. */
export const TAP_DRAG_THRESHOLD_PX = 8;

/** True when the pointer travelled far enough that this was a drag, not a tap. */
export function movedTooFarToBeATap(
  start: { x: number; y: number } | null,
  end: { x: number; y: number },
): boolean {
  if (!start) return false;
  return (
    Math.abs(end.x - start.x) > TAP_DRAG_THRESHOLD_PX ||
    Math.abs(end.y - start.y) > TAP_DRAG_THRESHOLD_PX
  );
}

/** Where a part of the card links to, when it links anywhere. */
export interface CardLinkTarget {
  /**
   * Already through `affiliateUrl` when it should be. Nothing in this file
   * wraps a URL: only the caller knows whether the shop behind it is one we
   * have an arrangement with.
   */
  href: string;
  /**
   * The exact `rel`, composed by the caller, for the same reason. `sponsored`
   * is a disclosure of a paid relationship, and a constant here would put it
   * on links that earn nothing.
   */
  rel: string;
  label?: string;
  onActivate?: () => void;
}

export interface ProductCardShellProps {
  /** Every photograph, the main one first. Deduplicated by the caller. */
  images: readonly string[];
  /** What each photograph is called. Indexed, because only the first names the garment. */
  altFor: (index: number) => string;
  brand: string | null;
  name: string;
  /** Already formatted. Null when there is no price to print. */
  priceLabel: string | null;
  /** The regular price, struck through. Null unless this is a sale. */
  originalPriceLabel: string | null;
  /** The tighter card: a portrait photograph and smaller type, for a grid. */
  compact?: boolean;
  /** Arrows over the photographs. Defaults to the shop's rule. */
  showArrows?: boolean;
  slideLink?: CardLinkTarget | null;
  wordsLink?: CardLinkTarget | null;
  /** Anything else on the line under the name, such as how many outfits wear the piece. */
  meta?: React.ReactNode;
  /** Structured data and the like, rendered first and drawing nothing. */
  head?: React.ReactNode;
  /** Over the top left of the photograph. */
  badge?: React.ReactNode;
  /** Over the top right of the photograph, such as the save button. */
  action?: React.ReactNode;
  /** Everything under the words: descriptions, attributes, buttons. */
  children?: React.ReactNode;
  testId?: string;
  slideTestId?: string;
  wordsTestId?: string;
  arrowTestIds?: { previous: string; next: string };
}

export default function ProductCardShell({
  images,
  altFor,
  brand,
  name,
  priceLabel,
  originalPriceLabel,
  compact = false,
  showArrows,
  slideLink = null,
  wordsLink = null,
  meta = null,
  head = null,
  badge = null,
  action = null,
  children = null,
  testId,
  slideTestId,
  wordsTestId,
  arrowTestIds,
}: ProductCardShellProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const pressRef = React.useRef<{ x: number; y: number } | null>(null);

  const scrollCarousel = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        window.scrollBy({ top: e.deltaY, left: 0 });
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const arrows = showArrows ?? (images.length > 1 && !compact);

  return (
    <Card
      data-testid={testId}
      className="group relative bg-card rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden flex flex-col h-full"
    >
      {head}
      <div
        className={cn(
          // Portrait at every size. Product photographs are portrait, and a
          // square box at desktop cropped the head and the shoes off every one
          // of them on the home page rail and the shop grid.
          "relative w-full flex-shrink-0 aspect-[3/4]",
        )}
      >
        <div
          ref={scrollRef}
          className="absolute inset-0 flex overflow-x-auto overflow-y-hidden overscroll-x-contain scrollbar-hide snap-x snap-mandatory"
        >
          {images.map((src, index) => {
            const photo = (
              <img
                src={src}
                alt={altFor(index)}
                className="w-full h-full flex-shrink-0 snap-start object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            );
            if (!slideLink) return <React.Fragment key={index}>{photo}</React.Fragment>;
            return (
              <a
                key={index}
                href={slideLink.href}
                target="_blank"
                rel={slideLink.rel}
                // Only the first photograph is reachable and named. The rest
                // are more views of one garment, and four anchors to the same
                // shop is four announcements of it.
                aria-label={index === 0 ? slideLink.label : undefined}
                aria-hidden={index === 0 ? undefined : true}
                tabIndex={index === 0 ? undefined : -1}
                data-testid={slideTestId}
                className="w-full h-full flex-shrink-0 snap-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                onPointerDown={(event) => {
                  pressRef.current = { x: event.clientX, y: event.clientY };
                }}
                onClick={(event) => {
                  const dragged = movedTooFarToBeATap(pressRef.current, {
                    x: event.clientX,
                    y: event.clientY,
                  });
                  pressRef.current = null;
                  if (dragged) {
                    event.preventDefault();
                    return;
                  }
                  slideLink.onActivate?.();
                }}
              >
                {photo}
              </a>
            );
          })}
        </div>
        {arrows && (
          <>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                scrollCarousel(-1);
              }}
              aria-label="Previous image"
              data-testid={arrowTestIds?.previous}
              className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/90 dark:bg-gray-900/90 flex items-center justify-center shadow-sm hover:bg-white dark:hover:bg-gray-900 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-charcoal dark:text-white" />
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                scrollCarousel(1);
              }}
              aria-label="Next image"
              data-testid={arrowTestIds?.next}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/90 dark:bg-gray-900/90 flex items-center justify-center shadow-sm hover:bg-white dark:hover:bg-gray-900 transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-charcoal dark:text-white" />
            </button>
          </>
        )}
      </div>

      {badge}
      {action}

      <CardContent className={cn("flex flex-col flex-1", compact ? "p-3" : "p-6")}>
        <CardWords
          brand={brand}
          name={name}
          priceLabel={priceLabel}
          originalPriceLabel={originalPriceLabel}
          compact={compact}
          meta={meta}
          link={wordsLink}
          testId={wordsTestId}
        />
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * The brand, the price and the name.
 *
 * Split out because this is the half that had drifted: the Mum Hub printed a
 * 10px tracked brand line in grey and the shop printed a blush one in caps,
 * and the sale price was struck through in two different greys.
 *
 * Wrapped in an anchor when the whole card is the shop link, and in nothing at
 * all when it is not. A `<div>` that is sometimes an `<a>` would change the
 * shop's markup for no reason.
 */
function CardWords({
  brand,
  name,
  priceLabel,
  originalPriceLabel,
  compact,
  meta,
  link,
  testId,
}: {
  brand: string | null;
  name: string;
  priceLabel: string | null;
  originalPriceLabel: string | null;
  compact: boolean;
  meta: React.ReactNode;
  link: CardLinkTarget | null;
  testId?: string;
}) {
  const words = (
    <>
      <div className={cn("flex items-center justify-between", compact ? "mb-1" : "mb-2")}>
        <span
          className={cn(
            "font-medium text-soft-blush uppercase tracking-wide",
            compact ? "text-xs" : "text-sm",
          )}
        >
          {brand}
        </span>
        <div className={cn("flex items-center", compact ? "gap-1" : "gap-2")}>
          {originalPriceLabel && (
            <span
              data-testid={testId ? `${testId}-was` : undefined}
              className={cn("text-gray-500 dark:text-gray-400 line-through", compact ? "text-xs" : "text-sm")}
            >
              {originalPriceLabel}
            </span>
          )}
          {priceLabel && (
            <span
              data-testid={testId ? `${testId}-price` : undefined}
              className={cn(
                "font-semibold text-charcoal dark:text-white",
                compact ? "text-sm" : "text-lg",
              )}
            >
              {priceLabel}
            </span>
          )}
        </div>
      </div>

      <h3
        className={cn(
          "font-semibold text-charcoal dark:text-white line-clamp-2",
          compact ? "text-sm mb-1" : "text-lg mb-2",
        )}
      >
        {name}
      </h3>

      {meta ? (
        <p
          data-testid={testId ? `${testId}-meta` : undefined}
          className={cn(
            "text-gray-600 dark:text-gray-300",
            compact ? "text-xs mb-1" : "text-sm mb-2",
          )}
        >
          {meta}
        </p>
      ) : null}
    </>
  );

  if (!link) return words;

  return (
    <a
      href={link.href}
      target="_blank"
      rel={link.rel}
      aria-label={link.label}
      data-testid={testId ? `${testId}-shop` : undefined}
      onClick={link.onActivate}
      className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      {words}
    </a>
  );
}
